import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCanDeleteWebhook, type PlainWebhook } from "@/queries/webhook-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { DeleteWebhookDialog } from "@/components/webhooks/DeleteWebhookDialog";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button, type ButtonSize } from "@/components/flexkit/Button";

export const WebhookActionsDropdown: FC<{
    webhook: PlainWebhook;
    buttonSize: ButtonSize;
    onOpenChange?: (open: boolean) => void;
}> = ({ webhook, buttonSize, onOpenChange }) => {
    const { toast } = useToast();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { canDeleteWebhook } = useCanDeleteWebhook(webhook.id);

    const handleCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(webhook.id);
            toast({
                title: "Webhook ID copied to clipboard",
                description: webhook.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy webhook ID",
                description: formatError(error),
            });
        }
    }, [toast, webhook.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), []);

    return (
        <div
            className="flex h-9 items-center"
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            data-tracking-id-none
        >
            <DropdownActions
                triggerTestId="webhook-actions-dropdown-trigger"
                button={
                    <Button
                        variant="outline"
                        aria-label="More actions"
                        LeadingIcon={IconEllipsisHorizontal}
                        size={buttonSize}
                    />
                }
                onOpenChange={onOpenChange}
                contentClassName="w-48"
            >
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-testid="webhook-actions-dropdown-copy-id"
                    data-tracking-id="copy-id-webhook-actions-dropdown"
                >
                    Copy Webhook ID
                </DropdownMenuItem>
                {canDeleteWebhook && (
                    <DropdownMenuItem
                        variant="destructive"
                        onClick={handleDelete}
                        data-testid="webhook-actions-dropdown-delete"
                        data-tracking-id="delete-webhook-actions-dropdown"
                    >
                        Delete
                    </DropdownMenuItem>
                )}
            </DropdownActions>
            <DeleteWebhookDialog open={showDeleteModal} webhook={webhook} onClose={() => setShowDeleteModal(false)} />
        </div>
    );
};
